import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import useHttp from 'hooks/useHttp';
import { getMLAApplicationListRequestConfig } from 'api/getV2RequestConfig';

export type MLARole = 'master' | 'agent';

export enum MLAApplicationStatus {
  Pending = 0,
  Approve = 1,
  Reject = 2,
}

export enum MLAApplicationComment {
  None = 0,
  NotQualified = 1,
  Duplicated = 2,
}

export type MLASyncStatus = 'PENDING_SYNC' | 'SYNCED' | 'SYNC_FAILED';

export type MLAApplicationRowData = {
  id: number;
  name: string;
  accountGroup: string;
  role: MLARole;
  traderId: number;
  applicationDate: string;
  email: string;
  exp: number;
  expCompany: string;
  oneMonthNewAcc: number;
  perMonthTradingVolume: number;
  avgDeposit: number;
  expComment: string;
  serviceComment: string;
  status: MLAApplicationStatus;
  comment: MLAApplicationComment;
  syncStatus: MLASyncStatus | null;
  createdLtAgentId: number | null;
  mlaAgent: number;
  mlaMaster: number;
  mlaAgentTradingAccount: number;
};

// Poll while any row is PENDING_SYNC (processing); interval chosen to balance freshness vs load.
const POLLING_INTERVAL_MS = 10_000;

type MLAApplicationListResponse = {
  data: {
    Name: string;
    account_group: string;
    role: string;
    id: number;
    trader_id: number;
    application_date: string;
    email: string;
    exp: number;
    exp_company: string;
    one_month_new_acc: number;
    per_month_trading_volume: number;
    avg_deposit: number;
    exp_comment: string;
    service_comment: string;
    status: number;
    comment: MLAApplicationComment;
    sync_status: MLAApplicationRowData['syncStatus'];
    created_lt_agent_id: number | null;
    mla_agent: number;
    mla_master: number;
    mla_agent_trading_account: number;
  }[];
  paginate: {
    baseURL: string;
    currentPage: number;
    itemPerPage: number;
    lastPage: number;
    nextPageURL: string | null;
    prevPageURL: string | null;
    totalRecord: number;
  };
};

const mapListResponseToRows = (
  responseData: MLAApplicationListResponse
): MLAApplicationRowData[] => responseData.data.map(
  (item) => ({
    id: item.id,
    name: item.Name,
    accountGroup: item.account_group,
    role: item.role as MLARole,
    traderId: item.trader_id,
    applicationDate: item.application_date,
    email: item.email,
    exp: item.exp,
    expCompany: item.exp_company,
    oneMonthNewAcc: item.one_month_new_acc,
    perMonthTradingVolume: item.per_month_trading_volume,
    avgDeposit: item.avg_deposit,
    expComment: item.exp_comment,
    serviceComment: item.service_comment,
    status: item.status as MLAApplicationStatus,
    comment: item.comment as MLAApplicationComment,
    syncStatus: item.sync_status,
    createdLtAgentId: item.created_lt_agent_id,
    mlaAgent: item.mla_agent,
    mlaMaster: item.mla_master,
    mlaAgentTradingAccount: item.mla_agent_trading_account
  })
);

const useGetMLAApplicationList = (
  currentRole: MLARole,
  currentStatus: MLAApplicationStatus | null,
  currentPage: number,
  rowsPerPage: number,
  searchInput: string,
  lang: string,
  submittingApplicationIds: Set<number> = new Set()
): {
  rows: MLAApplicationRowData[];
  setRows: Dispatch<SetStateAction<MLAApplicationRowData[]>>;
  totalPageCount: number;
  isListLoading: boolean;
} => {
  const { sendRequest } = useHttp();

  const [rows, setRows] = useState<MLAApplicationRowData[]>([]);
  const [totalPageCount, setTotalPageCount] = useState<number>(0);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);

  // Monotonic id so slower responses from older queries cannot overwrite newer state.
  const latestRequestIdRef = useRef(0);

  const fetchList = useCallback((options?: { showLoading?: boolean }) => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    // Overlay only on user-driven query changes, not on background polling.
    if (options?.showLoading) setIsListLoading(true);

    const finishRequest = (): void => {
      if (requestId !== latestRequestIdRef.current) return;

      setIsListLoading(false);
    };

    const applyResponse = (responseData: MLAApplicationListResponse): void => {
      // Ignore stale success callbacks when tab/filter/page changed mid-flight.
      if (requestId !== latestRequestIdRef.current) return;

      const applicationRowsData = mapListResponseToRows(responseData);

      setRows(applicationRowsData);
      setTotalPageCount(responseData.paginate.lastPage);
      setIsListLoading(false);
    };

    sendRequest(
      getMLAApplicationListRequestConfig(
        currentRole,
        currentStatus,
        currentPage,
        rowsPerPage,
        searchInput,
        lang
      ),
      applyResponse,
      finishRequest
    );
  }, [sendRequest, currentRole, currentStatus, currentPage, rowsPerPage, searchInput, lang]);

  // Refetch whenever list query params change (role, status, page, search, etc.).
  useEffect(() => {
    fetchList({ showLoading: true });
  }, [fetchList]);

  const hasPendingSync = rows.some((row) => row.syncStatus === 'PENDING_SYNC');

  // avoid polling racing with in-flight approve/reject/create for the same row.
  const isPollingPaused = submittingApplicationIds.size > 0;

  useEffect(() => {
    if (!hasPendingSync || isPollingPaused) return undefined;

    const timer = setInterval(() => fetchList(), POLLING_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [hasPendingSync, isPollingPaused, fetchList]);

  return {
    rows,
    setRows,
    totalPageCount,
    isListLoading
  };
};

export default useGetMLAApplicationList;
